import {
  Group,
  Pagination,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import {
  IconClock,
  IconDisc,
  IconFlame,
  IconMusic,
  IconTrophy,
  IconUsers,
} from '@tabler/icons-react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useScrobbleStats, useScrobbles, useUserSonicProfile } from '~/api/queries'
import type { ScrobbleStats } from '~/api/types'
import { AnalysisStatsCard, ProbeStatsCard } from '~/components/blocks/AnalysisStats/AnalysisStats'
import { CoverArt } from '~/components/blocks/CoverArt/CoverArt'
import { SonicRadar } from '~/components/blocks/SonicRadar/SonicRadar'
import { SkeletonList } from '~/components/primitives/SkeletonList'
import { formatDate, formatListeningTime } from '~/lib/format'

export const Route = createFileRoute('/_app/history/')({
  component: HistoryPage,
})

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const summaryItems: {
  key: keyof Pick<
    ScrobbleStats,
    'totalScrobbles' | 'uniqueTracks' | 'uniqueArtists' | 'uniqueAlbums'
  >
  label: string
  icon: typeof IconMusic
}[] = [
  { key: 'totalScrobbles', label: 'Total Scrobbles', icon: IconMusic },
  { key: 'uniqueTracks', label: 'Unique Tracks', icon: IconDisc },
  { key: 'uniqueArtists', label: 'Unique Artists', icon: IconUsers },
  { key: 'uniqueAlbums', label: 'Unique Albums', icon: IconDisc },
]

function ActivityBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <div
        style={{
          width: '100%',
          height: 80,
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <div
          style={{
            width: '100%',
            height: `${pct}%`,
            minHeight: value > 0 ? 2 : 0,
            background: 'var(--app-accent-fill)',
            borderRadius: '4px 4px 0 0',
            transition: 'height 0.3s ease',
          }}
        />
      </div>
    </div>
  )
}

function StatsSection({
  stats,
  isLoading,
}: {
  stats: ScrobbleStats | undefined
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <Stack gap="md">
        <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
          <SkeletonList count={5} height={70} />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Skeleton h={200} radius="md" />
          <Skeleton h={200} radius="md" />
        </SimpleGrid>
      </Stack>
    )
  }

  if (!stats || stats.totalScrobbles === 0) return null

  const maxDow = Math.max(...stats.scrobblesByDayOfWeek)
  const maxHour = Math.max(...stats.scrobblesByHour)

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
        {summaryItems.map((item) => (
          <Paper key={item.key} withBorder p="md" radius="md">
            <Group gap="sm" wrap="nowrap">
              <item.icon size={20} stroke={1.5} color="var(--app-accent-fill)" />
              <div>
                <Text fw={700} size="xl" lh={1} style={{ color: 'var(--app-accent-secondary)' }}>
                  {stats[item.key].toLocaleString()}
                </Text>
                <Text size="xs" c="dimmed" mt={4}>
                  {item.label}
                </Text>
              </div>
            </Group>
          </Paper>
        ))}
        <Paper withBorder p="md" radius="md">
          <Group gap="sm" wrap="nowrap">
            <IconClock size={20} stroke={1.5} color="var(--app-accent-fill)" />
            <div>
              <Text fw={700} size="xl" lh={1} style={{ color: 'var(--app-accent-secondary)' }}>
                {formatListeningTime(stats.totalListeningSeconds)}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                Listening Time
              </Text>
            </div>
          </Group>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Paper withBorder p="md" radius="md">
          <Group gap="sm" wrap="nowrap">
            <IconFlame size={20} stroke={1.5} color="var(--app-accent-fill)" />
            <div>
              <Text fw={700} size="xl" lh={1} style={{ color: 'var(--app-accent-secondary)' }}>
                {stats.currentStreak}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                Current Streak (days)
              </Text>
            </div>
          </Group>
        </Paper>
        <Paper withBorder p="md" radius="md">
          <Group gap="sm" wrap="nowrap">
            <IconTrophy size={20} stroke={1.5} color="var(--app-accent-fill)" />
            <div>
              <Text fw={700} size="xl" lh={1} style={{ color: 'var(--app-accent-secondary)' }}>
                {stats.longestStreak}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                Longest Streak (days)
              </Text>
            </div>
          </Group>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper withBorder p="md" radius="md">
          <Text size="sm" fw={500} mb="sm">
            Scrobbles by Day
          </Text>
          <div style={{ display: 'flex', gap: 4 }}>
            {stats.scrobblesByDayOfWeek.map((count, i) => (
              <Tooltip key={DAY_LABELS[i]} label={`${DAY_LABELS[i]}: ${count.toLocaleString()}`}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <ActivityBar value={count} max={maxDow} />
                  <Text size="xs" c="dimmed" mt={4}>
                    {DAY_LABELS[i]}
                  </Text>
                </div>
              </Tooltip>
            ))}
          </div>
        </Paper>
        <Paper withBorder p="md" radius="md">
          <Text size="sm" fw={500} mb="sm">
            Scrobbles by Hour
          </Text>
          <div style={{ display: 'flex', gap: 2 }}>
            {stats.scrobblesByHour.map((count, i) => (
              <Tooltip
                key={`hour-${String(i).padStart(2, '0')}`}
                label={`${String(i).padStart(2, '0')}:00 — ${count.toLocaleString()}`}
              >
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <ActivityBar value={count} max={maxHour} />
                  {i % 6 === 0 && (
                    <Text size="xs" c="dimmed" mt={4}>
                      {String(i).padStart(2, '0')}
                    </Text>
                  )}
                </div>
              </Tooltip>
            ))}
          </div>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Paper withBorder p="md" radius="md">
          <Text size="sm" fw={500} mb="sm">
            Top Tracks
          </Text>
          <Stack gap="xs">
            {stats.topTracks.map((t, i) => (
              <Group key={t.trackId} gap="sm" wrap="nowrap">
                <Text size="xs" c="dimmed" w={16} ta="right">
                  {i + 1}
                </Text>
                <div style={{ width: 28, height: 28, flexShrink: 0 }}>
                  <CoverArt type="tracks" id={t.trackId} size={56} w={28} h={28} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    size="sm"
                    truncate
                    display="block"
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/tracks/$trackId"
                        params={{ trackId: String(t.trackId) }}
                      />
                    )}
                    c="inherit"
                    td="none"
                  >
                    {t.title}
                  </Text>
                  <Text
                    size="xs"
                    c="dimmed"
                    truncate
                    display="block"
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/artists/$artistId"
                        params={{ artistId: String(t.artistId) }}
                      />
                    )}
                    td="none"
                  >
                    {t.artistName}
                  </Text>
                </div>
                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                  {t.count}
                </Text>
              </Group>
            ))}
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="sm" fw={500} mb="sm">
            Top Artists
          </Text>
          <Stack gap="xs">
            {stats.topArtists.map((a, i) => (
              <Group key={a.artistId} gap="sm" wrap="nowrap">
                <Text size="xs" c="dimmed" w={16} ta="right">
                  {i + 1}
                </Text>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: '50%',
                    overflow: 'hidden',
                  }}
                >
                  <CoverArt type="artists" id={a.artistId} size={56} w={28} h={28} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    size="sm"
                    truncate
                    display="block"
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/artists/$artistId"
                        params={{ artistId: String(a.artistId) }}
                      />
                    )}
                    c="inherit"
                    td="none"
                  >
                    {a.artistName}
                  </Text>
                </div>
                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                  {a.count}
                </Text>
              </Group>
            ))}
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="sm" fw={500} mb="sm">
            Top Albums
          </Text>
          <Stack gap="xs">
            {stats.topAlbums.map((al, i) => (
              <Group key={al.albumId} gap="sm" wrap="nowrap">
                <Text size="xs" c="dimmed" w={16} ta="right">
                  {i + 1}
                </Text>
                <div style={{ width: 28, height: 28, flexShrink: 0 }}>
                  <CoverArt type="albums" id={al.albumId} size={56} w={28} h={28} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    size="sm"
                    truncate
                    display="block"
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/albums/$albumId"
                        params={{ albumId: String(al.albumId) }}
                      />
                    )}
                    c="inherit"
                    td="none"
                  >
                    {al.albumName}
                  </Text>
                  <Text
                    size="xs"
                    c="dimmed"
                    truncate
                    display="block"
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/artists/$artistId"
                        params={{ artistId: String(al.artistId) }}
                      />
                    )}
                    td="none"
                  >
                    {al.artistName}
                  </Text>
                </div>
                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                  {al.count}
                </Text>
              </Group>
            ))}
          </Stack>
        </Paper>
      </SimpleGrid>
    </Stack>
  )
}

function HistoryPage() {
  const [page, setPage] = useState(1)
  const limit = 50
  const { data, isLoading } = useScrobbles({ limit, offset: (page - 1) * limit })
  const { data: stats, isLoading: statsLoading } = useScrobbleStats()
  const { data: sonicProfile } = useUserSonicProfile()

  const totalPages = data ? Math.ceil(data.total / limit) : 0

  return (
    <Stack gap="md">
      <Title order={2}>Play History</Title>

      <StatsSection stats={stats} isLoading={statsLoading} />

      {sonicProfile && sonicProfile.trackCount > 0 && (
        <section>
          <Title order={3} mb="md">
            Your Sonic Fingerprint
          </Title>
          <Paper withBorder p="md" radius="md">
            <Stack align="center" gap={8}>
              <SonicRadar profile={sonicProfile} />
              <Text size="xs" c="dimmed">
                Based on your top {sonicProfile.trackCount} track
                {sonicProfile.trackCount !== 1 ? 's' : ''}
              </Text>
            </Stack>
          </Paper>
        </section>
      )}

      <AnalysisStatsCard />
      <ProbeStatsCard />

      {isLoading ? (
        <Skeleton h={400} />
      ) : data?.data ? (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={50} />
              <Table.Th>Track</Table.Th>
              <Table.Th>Artist</Table.Th>
              <Table.Th>Album</Table.Th>
              <Table.Th>Played At</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.data.map((scrobble) => (
              <Table.Tr key={scrobble.id}>
                <Table.Td>
                  {scrobble.track.coverArt && (
                    <div style={{ width: 32, height: 32 }}>
                      <CoverArt type="tracks" id={scrobble.trackId} size={64} w={32} h={32} />
                    </div>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text
                    size="sm"
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/tracks/$trackId"
                        params={{ trackId: String(scrobble.trackId) }}
                      />
                    )}
                    c="inherit"
                    td="none"
                  >
                    {scrobble.track.title}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    size="sm"
                    c="dimmed"
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/artists/$artistId"
                        params={{ artistId: String(scrobble.track.artistId) }}
                      />
                    )}
                    td="none"
                  >
                    {scrobble.track.artistName}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    size="sm"
                    c="dimmed"
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/albums/$albumId"
                        params={{ albumId: String(scrobble.track.albumId) }}
                      />
                    )}
                    td="none"
                  >
                    {scrobble.track.albumName}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {formatDate(scrobble.time)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : null}

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}
    </Stack>
  )
}
